import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelect } from '@wordpress/data';
import { Add, Close, Remove } from '@mui/icons-material';
import { Alert, Dialog } from '@mui/material';
import { __ } from '../../../shared/tools';
import { STORE_PAGES } from '../../../shared/woocommerce/settings';
import { getCurrency } from '../../../shared/woocommerce/price';
import { useStoreAddToCart } from '../../../shared/hooks/useStoreAddToCart';
import { Button } from '../../../button/block';
import { SelectAttribute } from './attribute-select';
import style from './style.module.scss';

export const CartControl = ({
    product,
    setPrice,
    isInStock,
    setIsInStock,
    setVariationId,
    settings = {},
    ...props
}) => {
    const { variations, variationAttributes, advice_description } = product;

    const [quantity, setItemQuantity] = useState(1);
    const [currentAttributes, setCurrentAttributes] = useState({});
    const [productId, setProductId] = useState(!product.variationAttributes ? product.id : null);
    const [open, setOpen] = useState(false);
    const [sku, setSku] = useState(product.sku);

    const { addToCart, addingToCart, success } = useStoreAddToCart(productId);

    const { removeNotice } = useDispatch('core/notices');
    const notices = useSelect((select) => select('core/notices').getNotices('wc/all-products'), []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setOpen(false);
        removeNotice('add-to-cart', 'wc/all-products');
        await addToCart(quantity);
    };

    const gtagAddToCart = useCallback(() => {
        if (window.gtag) {
            const gtm_price = (product.salePrice ? product.salePrice : product.regularPrice) / 100;
            let gtagItem = {
                item_id: sku,
                item_name: product.name,
                discount: product.regularPrice - product.salePrice,
                index: 0,
                item_brand: 'Paraboot',
                price: gtm_price,
                quantity: quantity,
            };

            product.categories.forEach((category, index) => {
                gtagItem['item_category' + (index > 0 ? index + 1 : '')] = category;
            });

            window.gtag('event', 'add_to_cart', {
                currency: getCurrency().code,
                value: gtm_price,
                items: [gtagItem],
            });
        }
    }, [product, quantity, sku]);

    useEffect(() => {
        if (success) gtagAddToCart();
        setOpen(success === true);
    }, [success, gtagAddToCart]);

    const handleAttribute = useCallback(
        (slug, value) => {
            const newAttributes = { ...currentAttributes };
            newAttributes[slug] = value;
            setCurrentAttributes(newAttributes);
        },
        [setCurrentAttributes, currentAttributes],
    );

    const handleQuantityDown = () => setItemQuantity((old) => Math.max(old - 1, 1));

    const handleQuantityUp = () => setItemQuantity((old) => old + 1);

    const handleQuantity = (e) => setItemQuantity(parseInt(e.target.value));

    const handleClose = () => setOpen(false);

    useEffect(() => {
        if (!variationAttributes || !variations) return;

        if (Object.keys(variationAttributes).length === Object.keys(currentAttributes).length) {
            // Récupère la variation choisie
            const currentVariation = variations.find((variation) => {
                let found = true;
                Object.entries(currentAttributes).forEach(([key, value]) => {
                    if (
                        !variation.attributes['attribute_' + key] ||
                        variation.attributes['attribute_' + key] !== value
                    ) {
                        found = false;
                    }
                });
                return found;
            });

            if (currentVariation) {
                setPrice({
                    regular: currentVariation.display_regular_price * 100,
                    sale: currentVariation.display_price * 100,
                });

                setProductId(currentVariation.variation_id);
                setVariationId(currentVariation.variation_id);
                setSku(currentVariation.variation_sku);
                setIsInStock(currentVariation.is_in_stock);
            }
        }
    }, [setPrice, setProductId, setVariationId, setIsInStock, variationAttributes, variations, currentAttributes]);

    return (
        <form onSubmit={handleSubmit} className={style.container}>
            <div className={style.lastRow}>
                {variationAttributes && (
                    <div className={style.attributes}>
                        {Object.keys(variationAttributes).map((slug) => (
                            <SelectAttribute
                                key={slug}
                                slug={slug}
                                onChange={handleAttribute}
                                product={product}
                                settings={settings}
                            />
                        ))}
                    </div>
                )}
                {!(
                    product.categories.some((str) => str.toLowerCase() === 'accessoires') && !product.stock.isInStock
                ) && (
                    <>
                        <div className={style.quantity}>
                            <Remove onClick={handleQuantityDown} className={style.control} role="button" />
                            <input
                                type="number"
                                value={quantity}
                                className={style.inputQuantity}
                                onChange={handleQuantity}
                            />
                            <Add onClick={handleQuantityUp} className={style.control} role="button" />
                        </div>
                        <Button
                            tagName="button"
                            isSubmit={true}
                            text={__('Ajouter au panier')}
                            disabled={addingToCart || !isInStock}
                            className={style.btn}
                            type="filled"
                        />
                    </>
                )}
            </div>
            {advice_description && <p className={style.description}>{__(advice_description, 'paraboot')}</p>}
            {notices.length > 0 &&
                notices.map((notice, index) => (
                    <Alert key={'add_to_cart_notice_' + index} severity={notice.status} className={style.alert}>
                        {__(notice.content)}
                    </Alert>
                ))}
            <CartSuccessDialog product={product} open={open} onClose={handleClose} />
        </form>
    );
};

export const CartSuccessDialog = ({ product, open, onClose }) => {
    return (
        <Dialog open={open} onClose={onClose} className={style.dialog} PaperProps={{ className: style.content }}>
            <div className={style.header}>
                <p>{__('Le produit “{{product}}” a bien été ajouté à votre panier.', { product: product.name })}</p>
                <Close className={style.close} onClick={onClose} />
            </div>
            <Button
                text={__('Voir le panier')}
                className={style.btn}
                type="noborder"
                link={STORE_PAGES.cart.permalink}
            />
        </Dialog>
    );
};

export default CartControl;
